import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { BellOff, X } from 'lucide-react';
import { useI18n } from '../../../i18n/I18nProvider';
import { fetchQuietModeStatus, QUIET_MODE_EVENT } from './api';
import './QuietModeBubble.css';

/**
 * Quiet mode, said quietly (owner, 2026-10-03): a small pill in the bottom-right corner,
 * not a bar across every page. Only for people who manage things -- super admins, admins,
 * HR and content editors -- since they are the ones whose actions would otherwise tell
 * people something. Members see nothing.
 *
 * - The pill opens a tiny popover: "Nobody is notified", and Manage for super admins.
 * - "Hide for now" collapses it to an icon-only dot for the rest of the browser session.
 * - On a phone (under 600px) the pill is only its icon (owner, 2026-10-13): the text pill
 *   covered content. A tap opens the same popover, which says what it is.
 * - It keeps clear of anything fixed at the bottom of the page (the event editor's Save
 *   bar, the clean-up selection bar): it measures what sits under its corner and rises
 *   above it. A footer inside a taller fixed layer (the Events filter sheet's "Show 12
 *   events" row) is marked with {@code data-bottom-bar} and counts too.
 * - It steps aside entirely while a layer marked {@code data-hides-quiet-bubble} is open (the
 *   Events filter sheet: rising above its footer still covered the sheet's own fields).
 */

const COLLAPSED_KEY = 'kudos.quietBubble.collapsed';
const GAP = 16;

function readCollapsed(): boolean {
  try { return sessionStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}

function writeCollapsed(v: boolean) {
  try {
    if (v) sessionStorage.setItem(COLLAPSED_KEY, '1');
    else sessionStorage.removeItem(COLLAPSED_KEY);
  } catch {
    // Private windows may refuse storage; the bubble then simply comes back next page.
  }
}

/** Whether quiet mode is on, for someone who manages things; false (and no request) for anyone else. */
export function useQuietModeStatus(eligible: boolean): { on: boolean; canManage: boolean } {
  const [state, setState] = useState({ on: false, canManage: false });
  useEffect(() => {
    if (!eligible) {
      setState({ on: false, canManage: false });
      return undefined;
    }
    let alive = true;
    fetchQuietModeStatus()
      .then((s) => { if (alive) setState({ on: !!s?.on, canManage: !!s?.can_manage }); })
      .catch(() => {});
    const onToggle = (e: Event) => setState((prev) => ({ ...prev, on: !!(e as CustomEvent).detail?.on }));
    window.addEventListener(QUIET_MODE_EVENT, onToggle);
    return () => {
      alive = false;
      window.removeEventListener(QUIET_MODE_EVENT, onToggle);
    };
  }, [eligible]);
  return state;
}

/**
 * How far above the bottom edge the bubble must sit so it covers nothing fixed there.
 * Looks at what is under the bottom-right corner; a fixed or sticky ancestor shorter than
 * half the screen (a footer bar, not a full-page layer) pushes the bubble above its top.
 */
export function bottomClearance(self: HTMLElement | null): number {
  if (typeof document.elementsFromPoint !== 'function') return GAP;
  const vh = window.innerHeight;
  // Inside the pill and the collapsed dot (both end 16px from the edge): a card that stops
  // 24px from the edge, like the help welcome card, is under it and must be found.
  const x = window.innerWidth - 40;
  let top = vh;
  // A few points up from the corner: a bar that floats a little above the edge (the
  // clean-up selection bar sits 16px up) is found too.
  for (const dy of [4, 24, 44, 64]) {
    for (const hit of document.elementsFromPoint(x, vh - dy)) {
      if (self && self.contains(hit)) continue;
      for (let el: Element | null = hit; el && el !== document.body; el = el.parentElement) {
        if (el.hasAttribute('data-bottom-bar')) {
          const r = el.getBoundingClientRect();
          if (r.height > 0) top = Math.min(top, r.top);
          break;
        }
        const pos = getComputedStyle(el).position;
        if (pos !== 'fixed' && pos !== 'sticky') continue;
        const r = el.getBoundingClientRect();
        if (r.height > 0 && r.height < vh / 2 && r.bottom >= vh - 80) top = Math.min(top, r.top);
        break;
      }
    }
  }
  return vh - top + GAP;
}

/** Whether a layer that asks for the bubble to step aside is open. */
export function hiddenByLayer(): boolean {
  return !!document.querySelector('[data-hides-quiet-bubble]');
}

export default function QuietModeBubble({ canManage }: { canManage: boolean }) {
  const { t } = useI18n();
  const uid = useId().replace(/:/g, '');
  const rootRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [bottom, setBottom] = useState(GAP);
  const [covered, setCovered] = useState(false);

  // Re-measured whenever the page changes under it: a footer appearing, a resize, a scroll.
  useLayoutEffect(() => {
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setBottom(bottomClearance(rootRef.current));
        setCovered(hiddenByLayer());
      });
    };
    measure();
    const mo = new MutationObserver(measure);
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    // A sheet sliding up is still below the edge when it is added; measure again once it lands.
    window.addEventListener('transitionend', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('transitionend', measure, true);
    };
  }, []);

  // The app footer reads this to leave the bubble's corner free (features/legal/legal.css).
  useEffect(() => {
    document.documentElement.setAttribute('data-quiet-bubble', '');
    return () => document.documentElement.removeAttribute('data-quiet-bubble');
  }, []);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) pillRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(true); };
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, close]);

  const hide = () => {
    writeCollapsed(true);
    setCollapsed(true);
    setOpen(false);
    requestAnimationFrame(() => pillRef.current?.focus());
  };

  const expand = () => {
    writeCollapsed(false);
    setCollapsed(false);
    setOpen(true);
  };

  return (
    <div className="qmb" ref={rootRef} hidden={covered} data-tour="quiet-mode-bubble" style={{ bottom: `calc(${bottom}px + env(safe-area-inset-bottom))` }}>
      {open && !collapsed && (
        <div className="qmb-pop" id={`qmb-pop-${uid}`} role="dialog" aria-labelledby={`qmb-pop-title-${uid}`}>
          <p className="qmb-pop__title" id={`qmb-pop-title-${uid}`}>{t('platformOps.quiet.bubbleTitle')}</p>
          <p className="qmb-pop__body">{t('platformOps.quiet.bubbleBody')}</p>
          <div className="qmb-pop__actions">
            {canManage ? (
              <Link className="qmb-pop__link" to="/admin#quiet-mode" onClick={() => close(false)}>
                {t('platformOps.quiet.bannerLink')}
              </Link>
            ) : (
              <span className="qmb-pop__hint">{t('platformOps.quiet.bubbleAskSuperAdmin')}</span>
            )}
            <button type="button" className="qmb-pop__hide" onClick={hide}>
              {t('platformOps.quiet.bubbleHide')}
            </button>
          </div>
          <button type="button" className="qmb-pop__close" onClick={() => close(true)} aria-label={t('common.close')}>
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      )}
      {collapsed ? (
        <button
          type="button"
          ref={pillRef}
          className="qmb-dot"
          onClick={expand}
          aria-label={t('platformOps.quiet.bubbleDotLabel')}
          title={t('platformOps.quiet.bubbleDotLabel')}
        >
          <BellOff size={14} aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          ref={pillRef}
          className={`qmb-pill${open ? ' qmb-pill--open' : ''}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={open ? `qmb-pop-${uid}` : undefined}
          aria-label={t('platformOps.quiet.bubbleLabel')}
        >
          <BellOff size={14} aria-hidden="true" />
          {/* On a phone only the icon shows, so the pill covers nothing; a tap opens the same popover. */}
          <span className="qmb-pill__text">{t('platformOps.quiet.bubble')}</span>
        </button>
      )}
    </div>
  );
}
